/**
 * Chasis Vendidos x Mayor — migración web de ares9755.
 *
 * Listado de los chasis vendidos por el módulo Ventas x Mayor en modo
 * detallado (un chasis por línea, con su factura) o resumido (una línea
 * por factura con cantidad y balance). La acción "Saldar" marca el
 * chasis con chasispagado='S', lo que habilita el chasis para el cobro
 * de matrícula en modo mayor; solo la ven usuarios AdmG (el backend
 * re-valida el permiso y que la factura tenga abonos suficientes).
 * Exporta a Excel e imprime en ambos modos; en detallado también exporta
 * el Excel por factura (chasis agrupados por factura, con NCF y descripción).
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { fechaDMY, useApi } from "lib/api";
import { hayOtraMoneda, monedaLabel } from "lib/moneda";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
function imprimirListado(titulo, subtitulo, cabeceras, filas, totales) {
  imprimirListadoGrande({ titulo, subtitulo, cabeceras, filas, totales });
}

const moneyCol = (field, headerName, width = 110) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

export default function ChasisVendidosXmayor() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [modo, setModo] = useState("detallado");
  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [chasis, setChasis] = useState("");
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [config, setConfig] = useState(null);

  const [filas, setFilas] = useState([]);
  const [totales, setTotales] = useState(null);
  const [modoProc, setModoProc] = useState("detallado"); // modo del último proceso
  // Moneda en la que el backend ya expresó los importes.
  const [base, setBase] = useState("DOP");
  const [loading, setLoading] = useState(false);

  const [target, setTarget] = useState(null); // fila a saldar
  const [saldando, setSaldando] = useState(false);

  useEffect(() => {
    post("/api/xmayor_clientes", {}).then(({ ok, data }) => {
      if (ok) setClientes(data.clientes || []);
    });
    post("/api/xmayor_config", {}).then(({ ok, data }) => {
      if (ok) setConfig(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/xmayor_chasisvendidos", {
        modo,
        fechadesde: fecha1,
        fechahasta: fecha2,
        chasis: chasis.trim(),
        cliente: cliente?.id || "",
      });
      if (ok) {
        setFilas(data.filas || []);
        setBase(data.base || "DOP");
        setTotales({
          total: data.total || 0,
          pagado: data.pagado || 0,
          balance: data.balance || 0,
        });
        setModoProc(modo);
      } else {
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const saldar = async () => {
    setSaldando(true);
    try {
      const { ok, data } = await post("/api/xmayor_saldarchasis", {
        id: target.id,
        chasis: target.chasis,
      });
      if (ok) {
        showAlert({ message: `Chasis ${target.chasis} saldado`, color: "success" });
        setTarget(null);
        await procesar();
      } else {
        showAlert({ message: data.Error || "No se pudo saldar el chasis", color: "error" });
      }
    } finally {
      setSaldando(false);
    }
  };

  const esDetallado = modoProc === "detallado";
  const subtitulo = `${
    esDetallado ? `Del ${fechaDMY(fecha1)} al ${fechaDMY(fecha2)}` : `Al ${fechaDMY(fecha2)}`
  }${chasis.trim() ? ` · Chasis ${chasis.trim()}` : ""}${
    !esDetallado && cliente ? ` · ${cliente.descrip}` : ""
  }`;

  const exportar = () => {
    const stamp = hoyISO();
    let hoja;
    if (esDetallado) {
      hoja = XLSX.utils.aoa_to_sheet([
        ["Chasis Vendidos x Mayor — Detallado"],
        [subtitulo],
        [],
        [
          "Fact#",
          "Fecha",
          "Chasis",
          "Marca",
          "Modelo",
          "Color",
          "Suplidor",
          "Cliente",
          "Valor",
          "Precio Motor",
          "Estado",
          "Suc.",
        ],
        ...filas.map((r) => [
          r.id,
          r.fecha,
          r.chasis,
          r.marca,
          r.modelo,
          r.colore,
          r.suplidor,
          r.cliente,
          r.valor,
          r.preciomotor,
          r.chasispagado === "S" ? "Saldado" : "Pendiente",
          r.sucursal,
        ]),
        [],
        ["", "", "", "", "", "", "", "TOTAL", totales?.total || 0, "", "", ""],
      ]);
    } else {
      hoja = XLSX.utils.aoa_to_sheet([
        ["Chasis Vendidos x Mayor — Resumido"],
        [subtitulo],
        [],
        [
          "Fact#",
          "Fecha",
          "Suplidor",
          "Cliente",
          "Cantidad",
          "Valor",
          "Pagado",
          "Balance",
          "Estado",
          "Suc.",
        ],
        ...filas.map((r) => [
          r.id,
          r.fecha,
          r.suplidor,
          r.cliente,
          r.cantidad,
          r.valor,
          r.pagado,
          r.balance,
          r.status,
          r.sucursal,
        ]),
        [],
        [
          "",
          "",
          "",
          "TOTAL",
          "",
          totales?.total || 0,
          totales?.pagado || 0,
          totales?.balance || 0,
          "",
          "",
        ],
      ]);
    }
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, esDetallado ? "Detallado" : "Resumido");
    XLSX.writeFile(libro, `ChasisVendidosXmayor_${stamp}.xlsx`);
  };

  // Excel con el formato de las facturas x mayor: una fila por chasis
  // agrupada por factura (cliente, RNC, NCF y fecha solo en la primera
  // línea), precio en la moneda de la factura y en la moneda base, y un
  // subtotal por factura.
  const exportarPorFactura = () => {
    const anoTexto = (v) => (Number(v) > 0 ? String(v).trim() : "");
    const num = (v) => {
      const n = parseFloat(String(v ?? "").replace(/,/g, ""));
      return Number.isNaN(n) ? 0 : n;
    };
    const etqBase = monedaLabel(base);
    const filasXls = [
      ["Facturas x Mayor"],
      [subtitulo],
      [],
      [
        "Fact#",
        "Cod. cliente",
        "Cliente",
        "RNC",
        "NCF",
        "Fecha",
        "Chasis",
        "Moneda",
        "Precio",
        `Precio ${etqBase}`,
        "Descripción",
      ],
    ];
    // El backend ordena por fecha y chasis: dos facturas del mismo día
    // llegan intercaladas, por eso se agrupa por Fact# conservando el orden.
    const grupos = new Map();
    filas.forEach((r) => {
      if (!grupos.has(r.id)) grupos.set(r.id, []);
      grupos.get(r.id).push(r);
    });
    let gOrig = 0;
    let gBase = 0;
    const monedas = new Set(filas.map((r) => monedaLabel(r.moneda)));
    grupos.forEach((lineas) => {
      let sOrig = 0;
      let sBase = 0;
      lineas.forEach((r, idx) => {
        const primera = idx === 0;
        const orig = num(r.preciooriginal);
        const enBase = num(r.preciomotor);
        sOrig += orig;
        sBase += enBase;
        const descripcion = [r.marca, r.modelo, r.colore, anoTexto(r.ano), r.maquina]
          .map((x) => String(x || "").trim())
          .filter(Boolean)
          .join(" ");
        filasXls.push([
          primera ? r.id : "",
          primera ? r.suplidor : "",
          primera ? r.cliente : "",
          primera ? r.rnc : "",
          primera ? r.ncf : "",
          primera ? r.fecha : "",
          r.chasis,
          monedaLabel(r.moneda),
          orig,
          enBase,
          descripcion,
        ]);
      });
      filasXls.push(["", "", "", "", "", "", "", "", sOrig, sBase, ""]);
      gOrig += sOrig;
      gBase += sBase;
    });
    filasXls.push([]);
    // Con facturas en monedas distintas solo la columna en base suma bien.
    filasXls.push(["", "", "", "", "", "", "TOTAL", "", monedas.size > 1 ? "" : gOrig, gBase, ""]);
    const hoja = XLSX.utils.aoa_to_sheet(filasXls);
    // Montos con separador de miles y 2 decimales (filas de datos en adelante).
    for (let fila = 4; fila < filasXls.length; fila += 1) {
      [8, 9].forEach((col) => {
        const celda = hoja[XLSX.utils.encode_cell({ r: fila, c: col })];
        if (celda && celda.t === "n") celda.z = "#,##0.00";
      });
    }
    hoja["!cols"] = [
      { wch: 8 },
      { wch: 11 },
      { wch: 34 },
      { wch: 13 },
      { wch: 15 },
      { wch: 11 },
      { wch: 20 },
      { wch: 8 },
      { wch: 12 },
      { wch: 14 },
      { wch: 50 },
    ];
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Facturas");
    XLSX.writeFile(libro, `FacturasXmayor_${hoyISO()}.xlsx`);
  };

  const imprimir = () => {
    if (esDetallado) {
      imprimirListado(
        "Chasis Vendidos x Mayor — Detallado",
        subtitulo,
        [
          "Fact#",
          "Fecha",
          "Chasis",
          "Marca",
          "Modelo",
          "Cliente",
          "Precio Motor",
          "Estado",
          "Suc.",
        ],
        filas.map((r) => [
          r.id,
          r.fecha,
          r.chasis,
          r.marca,
          r.modelo,
          r.cliente,
          fmtMoney(r.preciomotor),
          r.chasispagado === "S" ? "Saldado" : "Pendiente",
          r.sucursal,
        ]),
        ["", "", "", "", "", "TOTAL", fmtMoney(totales?.total), "", ""]
      );
    } else {
      imprimirListado(
        "Chasis Vendidos x Mayor — Resumido",
        subtitulo,
        ["Fact#", "Fecha", "Cliente", "Cantidad", "Valor", "Pagado", "Balance", "Estado", "Suc."],
        filas.map((r) => [
          r.id,
          r.fecha,
          r.cliente,
          r.cantidad,
          fmtMoney(r.valor),
          fmtMoney(r.pagado),
          fmtMoney(r.balance),
          r.status,
          r.sucursal,
        ]),
        [
          "",
          "",
          "TOTAL",
          "",
          fmtMoney(totales?.total),
          fmtMoney(totales?.pagado),
          fmtMoney(totales?.balance),
          "",
          "",
        ]
      );
    }
  };

  // Columna solo si hay facturas en otra moneda (ver aging).
  const mezcla = hayOtraMoneda(filas, base);
  const colMoneda = {
    field: "moneda",
    headerName: "Moneda",
    width: 90,
    valueFormatter: (value) => monedaLabel(value),
    description: `Moneda de emisión. Los importes se muestran en ${monedaLabel(base)}.`,
  };

  const columnasDetallado = [
    { field: "id", headerName: "Fact#", width: 75 },
    { field: "fecha", headerName: "Fecha", width: 100 },
    { field: "chasis", headerName: "Chasis", width: 175 },
    ...(mezcla ? [colMoneda] : []),
    { field: "marca", headerName: "Marca", width: 100 },
    { field: "modelo", headerName: "Modelo", width: 110 },
    { field: "colore", headerName: "Color", width: 90 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 160 },
    moneyCol("preciomotor", "Precio Motor", 120),
    {
      field: "chasispagado",
      headerName: "Estado",
      width: 105,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.chasispagado === "S" ? (
          <Chip
            size="small"
            label="Saldado"
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Pendiente"
            sx={{ bgcolor: "#FFF8E1", color: "#B26A00", fontWeight: 700 }}
          />
        ),
    },
    { field: "sucursal", headerName: "Suc.", width: 65 },
    ...(config?.esadmin
      ? [
          {
            field: "acciones",
            headerName: "",
            width: 110,
            sortable: false,
            filterable: false,
            renderCell: (params) =>
              params.row.chasispagado !== "S" ? (
                <Button
                  size="small"
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={() => setTarget(params.row)}
                  sx={btnOutline(FP.color.success)}
                >
                  Saldar
                </Button>
              ) : null,
          },
        ]
      : []),
  ];

  const columnasResumido = [
    { field: "id", headerName: "Fact#", width: 80 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 180 },
    ...(mezcla ? [colMoneda] : []),
    {
      field: "cantidad",
      headerName: "Cantidad",
      width: 90,
      align: "right",
      headerAlign: "right",
    },
    moneyCol("valor", "Valor", 120),
    moneyCol("pagado", "Pagado", 120),
    moneyCol("balance", "Balance", 120),
    { field: "status", headerName: "Estado", width: 95 },
    { field: "sucursal", headerName: "Suc.", width: 70 },
  ];

  const hayDatos = filas.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Chasis Vendidos x Mayor"
        subtitle="Chasis facturados por ventas al por mayor, con su estado de saldo para el cobro de matrícula"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Modo"
                value={modo}
                onChange={(e) => setModo(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="detallado">Detallado</MenuItem>
                <MenuItem value="resumido">Resumido</MenuItem>
              </TextField>
            </Grid>
            {modo === "detallado" && (
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Desde"
                  InputLabelProps={{ shrink: true }}
                  value={fecha1}
                  onChange={(e) => setFecha1(e.target.value)}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="Chasis"
                placeholder="Opcional"
                value={chasis}
                onChange={(e) => setChasis(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            {modo === "resumido" && (
              <Grid item xs={12} sm={4} md={3}>
                <Autocomplete
                  options={clientes}
                  value={cliente}
                  onChange={(_, v) => setCliente(v)}
                  getOptionLabel={(o) => o?.descrip || ""}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Cliente (opcional)" sx={fpInputSx} />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip
              label="TOTAL VENDIDO"
              value={`${monedaLabel(base)} ${fmtMoney(totales?.total)}`}
            />
            {!esDetallado && (
              <>
                <ResumenChip
                  label="PAGADO"
                  value={`${monedaLabel(base)} ${fmtMoney(totales?.pagado)}`}
                  color={FP.color.success}
                />
                <ResumenChip
                  label="BALANCE"
                  value={`${monedaLabel(base)} ${fmtMoney(totales?.balance)}`}
                  color="#C62828"
                />
              </>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            {esDetallado && (
              <Button
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={exportarPorFactura}
                disabled={!hayDatos}
                sx={btnOutline(FP.color.success)}
              >
                Excel por factura
              </Button>
            )}
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={filas}
              columns={esDetallado ? columnasDetallado : columnasResumido}
              getRowId={esDetallado ? (r) => `${r.id}-${r.chasis}` : (r) => r.id}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Confirmación de saldar chasis */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.navy }}>Saldar chasis</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Chasis <b>{target?.chasis}</b> de la factura <b>#{target?.id}</b> ({target?.cliente}).
            Saldarlo lo marca como pagado y habilita el chasis para el cobro de la matrícula. ¿Desea
            continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              saldando ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />
            }
            onClick={saldar}
            disabled={saldando}
            sx={btnSolid(FP.color.success, "#2E7D32")}
          >
            Sí, saldar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
